// Copyright TraderEvolution Global LTD. © 2017-2026. All rights reserved.
import { OptionPutCall } from '@shared/utils/Instruments/OptionPutCall';
class _BlackSholesModel {
    // #region Technical Methods
    norm_dist(x) {
        const d1 = 0.049867347;
        const d2 = 0.0211410061;
        const d3 = 0.0032776263;
        const d4 = 0.0000380036;
        const d5 = 0.0000488906;
        const d6 = 0.000005383;
        const a = Math.abs(x);
        let t = 1.0 + a * (d1 + a * (d2 + a * (d3 + a * (d4 + a * (d5 + a * d6)))));
        t *= t;
        t *= t;
        t *= t;
        t *= t;
        t = 1.0 / (t + t);
        if (x >= 0) {
            t = 1 - t;
        }
        return t;
    }
    d1(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((Math.log(underlierPrice / strikePrice) + (interestRate + Math.pow(sigma, 2) / 2) * expiration) /
            (sigma * Math.sqrt(expiration)));
    }
    d2(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma) - sigma * Math.sqrt(expiration);
    }
    // #endregion
    // #region Private Methods
    call_price(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        const d1 = this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma);
        const d2 = this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma);
        return (underlierPrice * this.norm_dist(d1) -
            strikePrice * Math.exp(-(interestRate * expiration)) * this.norm_dist(d2));
    }
    put_price(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        const d1 = this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma);
        const d2 = this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma);
        return (strikePrice * Math.exp(-(interestRate * expiration)) * this.norm_dist(-d2) -
            underlierPrice * this.norm_dist(-d1));
    }
    call_delta(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return this.norm_dist(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma));
    }
    put_delta(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return this.norm_dist(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma)) - 1;
    }
    call_theta(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((-(underlierPrice *
            (Math.exp(-Math.pow(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma), 2) / 2) /
                Math.sqrt(2 * Math.PI)) *
            sigma) /
            (2 * Math.sqrt(expiration)) -
            interestRate *
                strikePrice *
                Math.exp(-(interestRate * expiration)) *
                this.norm_dist(this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma))) /
            365);
    }
    put_theta(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((-(underlierPrice *
            (Math.exp(-Math.pow(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma), 2) / 2) /
                Math.sqrt(2 * Math.PI)) *
            sigma) /
            (2 * Math.sqrt(expiration)) +
            interestRate *
                strikePrice *
                Math.exp(-(interestRate * expiration)) *
                this.norm_dist(-1 * this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma))) /
            365);
    }
    call_rho(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((strikePrice *
            expiration *
            Math.exp(-(interestRate * expiration)) *
            this.norm_dist(this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma))) /
            100);
    }
    put_rho(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((-strikePrice *
            expiration *
            Math.exp(-(interestRate * expiration)) *
            this.norm_dist(-1 * this.d2(underlierPrice, strikePrice, expiration, interestRate, sigma))) /
            100);
    }
    call_iv(optionPrice, underlierPrice, strikePrice, expiration, interestRate) {
        if (!isValidNumber(optionPrice) || optionPrice === 0) {
            return undefined;
        }
        if (!isValidNumber(underlierPrice) || underlierPrice === 0) {
            return undefined;
        }
        if (!isValidNumber(strikePrice) || strikePrice === 0) {
            return undefined;
        }
        if (expiration <= 0) {
            return undefined;
        }
        let callPrice = 0;
        let sigma = 0.00001;
        let increment = 0.1;
        let forceWhile = false;
        while (forceWhile || callPrice < optionPrice) {
            forceWhile = false;
            callPrice = this.call_price(underlierPrice, strikePrice, expiration, interestRate, sigma);
            if (callPrice > optionPrice && increment > 0.00001 && sigma > 0.0001) {
                sigma -= increment;
                increment *= 0.1;
                sigma += increment;
                forceWhile = true;
                continue;
            }
            else if (callPrice > optionPrice) {
                const res = callPrice - optionPrice;
                if (res > 0.0000000001) {
                    return sigma;
                }
            }
            sigma += increment;
            if (sigma > 10000) {
                return Infinity;
            }
        }
        return sigma;
    }
    put_iv(optionPrice, underlierPrice, strikePrice, expiration, interestRate) {
        if (!isValidNumber(optionPrice) || optionPrice === 0) {
            return undefined;
        }
        if (!isValidNumber(underlierPrice) || underlierPrice === 0) {
            return undefined;
        }
        if (!isValidNumber(strikePrice) || strikePrice === 0) {
            return undefined;
        }
        if (expiration <= 0) {
            return undefined;
        }
        let putPrice = 0;
        let sigma = 0.00001;
        let increment = 0.1;
        let forceWhile = false;
        while (forceWhile || putPrice < optionPrice) {
            forceWhile = false;
            putPrice = this.put_price(underlierPrice, strikePrice, expiration, interestRate, sigma);
            if (putPrice > optionPrice && increment > 0.00001 && sigma > 0.0001) {
                sigma -= increment;
                increment *= 0.1;
                sigma += increment;
                forceWhile = true;
                continue;
            }
            else if (putPrice > optionPrice) {
                const res = putPrice - optionPrice;
                if (res > 0.0000000001) {
                    return sigma;
                }
            }
            sigma += increment;
            if (sigma > 10000) {
                return Infinity;
            }
        }
        return sigma;
    }
    // #endregion
    // #region  IOptionCalculator
    gamma(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return (Math.exp(-Math.pow(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma), 2) / 2) /
            Math.sqrt(2 * Math.PI) /
            (underlierPrice * sigma * Math.sqrt(expiration)));
    }
    vega(underlierPrice, strikePrice, expiration, interestRate, sigma) {
        return ((underlierPrice *
            Math.sqrt(expiration) *
            (Math.exp(-Math.pow(this.d1(underlierPrice, strikePrice, expiration, interestRate, sigma), 2) / 2) /
                Math.sqrt(2 * Math.PI))) /
            100);
    }
    delta(putCall, underlierPrice, strikePrice, expiration, interestRate, sigma) {
        switch (putCall) {
            case OptionPutCall.OPTION_CALL_VANILLA:
                return this.call_delta(underlierPrice, strikePrice, expiration, interestRate, sigma);
            case OptionPutCall.OPTION_PUT_VANILLA:
                return this.put_delta(underlierPrice, strikePrice, expiration, interestRate, sigma);
            default:
                return undefined;
        }
    }
    theta(putCall, underlierPrice, strikePrice, expiration, interestRate, sigma) {
        switch (putCall) {
            case OptionPutCall.OPTION_CALL_VANILLA:
                return this.call_theta(underlierPrice, strikePrice, expiration, interestRate, sigma);
            case OptionPutCall.OPTION_PUT_VANILLA:
                return this.put_theta(underlierPrice, strikePrice, expiration, interestRate, sigma);
            default:
                return undefined;
        }
    }
    rho(putCall, underlierPrice, strikePrice, expiration, interestRate, sigma) {
        switch (putCall) {
            case OptionPutCall.OPTION_CALL_VANILLA:
                return this.call_rho(underlierPrice, strikePrice, expiration, interestRate, sigma);
            case OptionPutCall.OPTION_PUT_VANILLA:
                return this.put_rho(underlierPrice, strikePrice, expiration, interestRate, sigma);
            default:
                return undefined;
        }
    }
    iv(putCall, optionPrice, underlierPrice, strikePrice, expiration, interestRate) {
        switch (putCall) {
            case OptionPutCall.OPTION_CALL_VANILLA:
                return this.call_iv(optionPrice, underlierPrice, strikePrice, expiration, interestRate);
            case OptionPutCall.OPTION_PUT_VANILLA:
                return this.put_iv(optionPrice, underlierPrice, strikePrice, expiration, interestRate);
            default:
                return undefined;
        }
    }
    theoreticalPrice(putCall, underlierPrice, strikePrice, expiration, interestRate, iv) {
        switch (putCall) {
            case OptionPutCall.OPTION_CALL_VANILLA:
                return this.call_price(underlierPrice, strikePrice, expiration, interestRate, iv);
            case OptionPutCall.OPTION_PUT_VANILLA:
                return this.put_price(underlierPrice, strikePrice, expiration, interestRate, iv);
            default:
                return underlierPrice;
        }
    }
}
export const BlackSholesModel = new _BlackSholesModel();
